import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function Footer() {
  const socialLinks = [
    { id: 'social_instagram', label: 'Instagram', href: 'https://instagram.com', icon: 'CameraIcon' },
    { id: 'social_facebook', label: 'Facebook', href: 'https://facebook.com', icon: 'UserGroupIcon' },
    { id: 'social_twitter', label: 'Twitter', href: 'https://twitter.com', icon: 'ChatBubbleLeftIcon' },
  ];

  const aboutLinks = [
    { id: 'footer_our_story', label: 'Our Story', href: '/about' },
    { id: 'footer_team', label: 'Team', href: '/about#team' },
    { id: 'footer_press', label: 'Press', href: '/about#press' },
    { id: 'footer_careers', label: 'Careers', href: '/about#careers' },
  ];

  const visitLinks = [
    { id: 'footer_hours', label: 'Hours', href: '/about#hours' },
    { id: 'footer_location', label: 'Location', href: '/about#location' },
    { id: 'footer_parking', label: 'Parking', href: '/about#parking' },
    { id: 'footer_reservations', label: 'Reservations', href: '/homepage#reservation' },
  ];

  const menuLinks = [
    { id: 'footer_lunch', label: 'Lunch', href: '/menu#lunch' },
    { id: 'footer_dinner', label: 'Dinner', href: '/menu#dinner' },
    { id: 'footer_drinks', label: 'Drinks', href: '/menu#drinks' },
    { id: 'footer_catering', label: 'Catering', href: '/about#catering' },
  ];

  const connectLinks = [
    { id: 'footer_instagram', label: 'Instagram', href: 'https://instagram.com' },
    { id: 'footer_facebook', label: 'Facebook', href: 'https://facebook.com' },
    { id: 'footer_email', label: 'Email Us', href: 'mailto:hello@restaurantbrand.com' },
    { id: 'footer_phone', label: '(555) 123-4567', href: 'tel:5551234567' },
  ];

  return (
    <footer className="bg-foreground text-background border-t border-muted/20">
      {/* Social Links Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 border-b border-muted/20">
        {socialLinks.map((social) => (
          <a
            key={social.id}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between p-6 border-b md:border-b-0 md:border-r border-muted/20 last:border-r-0 hover:bg-muted/5 transition-colors"
          >
            <div className="flex items-center gap-3 text-background/80 group-hover:text-background">
              <Icon name={social.icon as any} size={18} />
              <span className="text-sm font-medium">{social.label}</span>
            </div>
            <Icon
              name="ArrowTopRightOnSquareIcon"
              size={16}
              className="text-muted/60 group-hover:text-background group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </a>
        ))}
      </div>

      {/* Links Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12 px-6 md:px-12 py-16">
        {/* About */}
        <div className="space-y-6">
          <h4 className="text-[11px] font-bold tracking-[0.15em] uppercase text-background/60">
            About RestaurantBrand
          </h4>
          <ul className="flex flex-col gap-3.5 text-sm text-background/80">
            {aboutLinks.map((link) => (
              <li key={link.id}>
                <Link href={link.href} className="hover:text-background transition-colors">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Visit */}
        <div className="space-y-6">
          <h4 className="text-[11px] font-bold tracking-[0.15em] uppercase text-background/60">
            Visit
          </h4>
          <ul className="flex flex-col gap-3.5 text-sm text-background/80">
            {visitLinks.map((link) => (
              <li key={link.id}>
                <Link href={link.href} className="hover:text-background transition-colors">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Menu */}
        <div className="space-y-6">
          <h4 className="text-[11px] font-bold tracking-[0.15em] uppercase text-background/60">
            Menu
          </h4>
          <ul className="flex flex-col gap-3.5 text-sm text-background/80">
            {menuLinks.map((link) => (
              <li key={link.id}>
                <Link href={link.href} className="hover:text-background transition-colors">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Connect */}
        <div className="space-y-6">
          <h4 className="text-[11px] font-bold tracking-[0.15em] uppercase text-background/60">
            Connect
          </h4>
          <ul className="flex flex-col gap-3.5 text-sm text-background/80">
            {connectLinks.map((link) => (
              <li key={link.id}>
                <a
                  href={link.href}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="hover:text-background transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between border-t border-muted/20 py-10 px-6 md:px-12 gap-6">
        <div className="flex items-center gap-3 opacity-60">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <Icon name="HomeIcon" size={16} variant="solid" className="text-primary-foreground" />
          </div>
          <span className="text-sm font-medium tracking-tight text-background/80">
            © 2026 RestaurantBrand.
          </span>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-background/60 text-sm">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse-dot"></div>
            Open Today: 11:00 AM - 10:00 PM
          </div>
        </div>

        <div className="flex items-center gap-6 text-sm text-background/60">
          <Link href="/privacy" className="hover:text-background transition-colors">
            Privacy
          </Link>
          <Link href="/terms" className="hover:text-background transition-colors">
            Terms
          </Link>
        </div>
      </div>
    </footer>
  );
}